// ============================================================
// TMR Portal Admin — primitivos visuais compartilhados
// ============================================================
import type { CSSProperties, ReactNode } from 'react';
import { StrokeIcon } from '../lib/icons';

// --- Money: BRL com "R$" em serifa itálica ---
export function Money({
  value,
  size = 16,
  color,
  sign = '',
  muted = false,
}: {
  value: number;
  size?: number;
  color?: string;
  sign?: string;
  muted?: boolean;
}) {
  const neg = value < 0;
  const [intPart, decPart] = Math.abs(value).toFixed(2).replace('.', ',').split(',');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const c = color || (muted ? 'var(--fg-muted)' : 'var(--ink-900)');
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        fontSize: size,
        fontWeight: 500,
        letterSpacing: '-0.01em',
        color: c,
        whiteSpace: 'nowrap',
      }}
    >
      {(sign || neg) && <span style={{ marginRight: 2 }}>{neg ? '−' : sign}</span>}
      {/* The currency used to be an italic serif inside the number; with the display face now mono,
          italic would be a slanted fake. It recedes by size and opacity instead. */}
      <span
        style={{
          fontSize: size * 0.72,
          color: c,
          opacity: 0.7,
          marginRight: 3,
          fontWeight: 400,
        }}
      >
        R$
      </span>
      {intFormatted}
      <span style={{ fontSize: size * 0.86 }}>,{decPart}</span>
    </span>
  );
}

// --- IconTile: caixa arredondada com fundo sage ---
export function IconTile({
  children,
  size = 38,
  bg = 'var(--sage-100)',
  color = 'var(--forest-900)',
  radius,
}: {
  children: ReactNode;
  size?: number;
  bg?: string;
  color?: string;
  radius?: number;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius || size * 0.28,
        background: bg,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

interface BadgeStyle {
  bg: string;
  fg: string;
  label: string;
}

const STATUS_MAP: Record<string, BadgeStyle> = {
  pago: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Pago' },
  liquidado: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Liquidado' },
  pendente: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Pendente' },
  emitido: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Emitido' },
  recusado: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(38% 0.12 28)', label: 'Recusado' },
  vencido: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(38% 0.12 28)', label: 'Vencido' },
  analise: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Em análise' },
  antecipado: { bg: 'var(--gold-300)', fg: 'var(--gold-700)', label: 'Antecipado' },
  ativo: { bg: 'var(--sage-100)', fg: 'var(--forest-800)', label: 'Ativo' },
  inativo: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Inativo' },
  convidado: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Convidado' },
  /* Sincronia da Empresa com o legado — derivada de `exportStatus` + `recordOrigin` por `syncState`
     (data/companies/status.ts). `Importada` e `Exportada` são o mesmo `Exported` do backend em direções
     opostas: uma veio de lá, a outra foi para lá — daí o mesmo verde de está-em-dia. */
  Pending: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Pendente' },
  Exported: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Exportada' },
  Imported: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Importada' },
  Failed: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(38% 0.12 28)', label: 'Falhou' },
  /* Solicitação de acesso (10 B7d) — o `Pending` acima serve às duas, com o mesmo significado. */
  Approved: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Aprovada' },
  Rejected: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Rejeitada' },
  /* Vida do convite (06 §6.1) — decisão E entrega lidas juntas, via `inviteSituation`. "Criado" é o
     único que significa que alguém consegue entrar; só ele ganha o verde de terminou-bem. */
  convite_aguardando: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Aguardando' },
  convite_fila: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Na fila' },
  convite_enviado: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Enviado' },
  convite_criado: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Criado' },
  convite_falhou: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(38% 0.12 28)', label: 'Falhou' },
  convite_rejeitado: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Rejeitada' },
  /* Na lista de Usuários: existe conta no Clerk por trás deste cadastro, ou não. Rótulos próprios
     porque ali não se sabe da entrega do convite — só se a pessoa chegou do outro lado. */
  acesso_criado: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Acesso criado' },
  acesso_aguardando: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Aguardando aceite' },
  /* Status de negócio da Empresa (10 B6a) — o principal na lista e no detalhe. As chaves não são os
     valores do backend porque `Rejected` já significa "solicitação rejeitada" acima; o mapa de tradução
     é o `BUSINESS_STATUS_BADGE` (data/companies/status.ts). Rótulos no feminino: a Empresa é que está
     concluída ou reprovada. */
  pendente_contrato: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Pendente contrato' },
  pendente_teste_conta: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Pendente teste de conta' },
  aguardando_assinatura: { bg: 'var(--gold-300)', fg: 'var(--gold-700)', label: 'Aguardando assinatura' },
  concluido: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Concluída' },
  reprovado: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(38% 0.12 28)', label: 'Reprovada' },
  /* Prefixada porque `inativo` acima já é a pessoa desativada, e aqui o rótulo é no feminino. Cinza, não
     vermelho: uma Empresa inativa não é um erro nem uma reprovação, é uma que foi desligada. */
  empresa_inativa: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Inativa' },
  postado: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Postado' },
  estornado: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(40% 0.13 28)', label: 'Estornado' },
  substituido: { bg: 'var(--gold-300)', fg: 'var(--gold-700)', label: 'Substituído' },
  conciliado: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Conciliado' },
  divergente: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(40% 0.13 28)', label: 'Divergente' },
  /* Ciclo do contrato (M3, 02 §5) — o mapa de tradução é o `CONTRACT_STATUS_BADGE`
     (data/contracts/status.ts). Prefixadas porque `Pending` acima já é replicação pendente E solicitação
     aguardando: um `Pending` cru aqui faria contrato-não-enviado ler como problema de sincronia. O amarelo
     de `contrato_pendente` é o mesmo de "pendente" em toda parte — é o estado em que um envio que falhou
     deixa o contrato, e o único acionável. */
  contrato_pendente: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Pendente' },
  contrato_aguardando: { bg: 'var(--gold-300)', fg: 'var(--gold-700)', label: 'Aguardando assinatura' },
  contrato_assinado: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Assinado' },
  contrato_recusado: { bg: 'oklch(94% 0.04 28)', fg: 'oklch(38% 0.12 28)', label: 'Recusado' },
  contrato_expirado: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Expirado' },
  contrato_cancelado: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Cancelado' },
  /* Janela da taxa temporária (08 §5) — traduzida por `TEMPORARY_STATE_BADGE` (data/rates/status.ts).
     Os rótulos são curtos porque a linha da taxa já escreve a frase inteira logo abaixo ("Taxa temporária
     vigente · 31/07 a 31/08"); o badge é para varrer a lista com os olhos, não para repeti-la. Vigente
     leva o âmbar de "repare nisto": é uma exceção em vigor, e ela vai acabar. */
  taxa_padrao: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Sem temporária' },
  taxa_agendada: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Agendada' },
  taxa_vigente: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Vigente' },
  taxa_encerrada: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Encerrada' },
  /* Solicitação de cadastro do site (28 §9) — traduzida por `APPLICATION_STATUS_BADGE`
     (data/companyApplications/status.ts). Prefixadas porque `Approved`/`Rejected` acima são da solicitação
     de acesso. Recebida leva o âmbar de "alguém precisa olhar"; reprovada é cinza, não vermelho: recusar um
     pedido é uma decisão, não um erro. */
  solicitacao_recebida: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Recebida' },
  solicitacao_em_analise: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Em análise' },
  solicitacao_aprovada: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Aprovada' },
  solicitacao_reprovada: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Reprovada' },
  /* A contingency request (31 §9), translated by `REQUEST_STATUS_BADGE` (data/contingency/status.ts).
     Prefixed for the same reason as the applications. Waiting wears the amber of "a manager has to look";
     approved is blue because the requester still has to generate the code; generated is the green of "done". */
  contingencia_aguardando: { bg: 'oklch(94% 0.05 80)', fg: 'oklch(40% 0.10 60)', label: 'Aguardando aprovação' },
  contingencia_aprovada: { bg: 'oklch(94% 0.03 230)', fg: 'oklch(34% 0.08 230)', label: 'Aprovado' },
  contingencia_reprovada: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: 'Reprovado' },
  contingencia_gerada: { bg: 'oklch(94% 0.04 155)', fg: 'oklch(34% 0.08 155)', label: 'Código gerado' },
};

/**
 * The badge vocabulary, exported so a test can prove every key a screen declares actually lives here.
 *
 * That guard is not ceremony. {@link StatusBadge} used to fall back to `pendente_contrato` for an
 * unknown key, so a status map that forgot to register its keys rendered every row as "Pendente
 * contrato" — a real, plausible label, which is why it read as a wrong status rather than as a bug. It
 * shipped exactly that way for the rate tab.
 */
export { STATUS_MAP };

// --- StatusBadge: pílula colorida ---
export function StatusBadge({ status }: { status: string }) {
  // An unregistered key shows itself, in neutral grey. It used to fall back to `pendente_contrato`, and
  // that is how the rate tab shipped every temporary window reading "Pendente contrato": the fallback was
  // a real status, so nothing looked broken — it looked wrong, which is much harder to notice. Showing
  // the raw key makes a missing registration obvious the first time anybody sees it.
  const s = STATUS_MAP[status] ?? { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: status };
  return (
    <span
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 11.5,
        fontWeight: 500,
        padding: '3px 9px',
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', opacity: 0.7 }} />
      {s.label}
    </span>
  );
}

// --- DeltaTag: variação percentual com seta ---
export function DeltaTag({ value, invert = false, size = 12.5 }: { value: number; invert?: boolean; size?: number }) {
  const up = value >= 0;
  const good = invert ? !up : up;
  const color = good ? 'oklch(46% 0.10 155)' : 'oklch(50% 0.15 28)';
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        fontSize: size,
        fontWeight: 500,
        color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        whiteSpace: 'nowrap',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: up ? 'none' : 'scaleY(-1)' }}
      >
        <path d="M7 14l5-5 5 5" />
      </svg>
      {up ? '+' : '−'}
      {Math.abs(value).toFixed(1).replace('.', ',')}%
    </span>
  );
}

// --- Avatar: iniciais sobre fundo derivado do nome ---
export function Avatar({ name, size = 34, ring = false }: { name: string; size?: number; ring?: boolean }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const hues = [165, 80, 230, 28, 200, 120];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % hues.length;
  const hue = hues[h];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: `oklch(90% 0.04 ${hue})`,
        color: `oklch(38% 0.09 ${hue})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-sans)',
        fontWeight: 600,
        fontSize: size * 0.38,
        boxShadow: ring ? '0 0 0 2px var(--cream-50), 0 0 0 3px var(--stone-200)' : 'none',
      }}
    >
      {initials}
    </div>
  );
}

/**
 * One choice in a {@link Segmented}. The bare value is the shorthand for "label it with itself".
 *
 * `disabled` shows the option and refuses it, rather than hiding it — an option that vanishes makes
 * "por que essa opção não aparece aqui?" unanswerable, and makes two environments look like different
 * products. Say why beside the control; the reason is never in a `title`, because a disabled button
 * swallows hover in some browsers.
 */
type SegmentOption<T> = T | { value: T; label: string; disabled?: boolean };

// --- Segmented control (período, sim/não etc.) ---
export function Segmented<T extends string | number | boolean>({
  options,
  value,
  onChange,
  size = 'md',
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
}) {
  const pad = size === 'sm' ? '5px 11px' : '7px 14px';
  const fs = size === 'sm' ? 12.5 : 13.5;
  return (
    <div
      className="pa-segmented"
      style={{
        display: 'inline-flex',
        background: 'var(--cream-200)',
        borderRadius: 'var(--radius-sm)',
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((o) => {
        const v = (typeof o === 'object' ? o.value : o) as T;
        const label = typeof o === 'object' ? o.label : String(o);
        const disabled = typeof o === 'object' && o.disabled === true;
        const active = v === value;
        return (
          <button
            key={String(v)}
            type="button"
            className="pa-segment"
            data-active={active || undefined}
            disabled={disabled}
            onClick={disabled ? undefined : () => onChange(v)}
            style={{
              border: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: pad,
              fontSize: fs,
              fontFamily: 'var(--font-sans)',
              fontWeight: 500,
              borderRadius: 4,
              background: active ? 'var(--cream-50)' : 'transparent',
              color: active ? 'var(--ink-900)' : 'var(--fg-muted)',
              boxShadow: active ? 'var(--shadow-sm)' : 'none',
              opacity: disabled ? 0.45 : 1,
              transition: 'all var(--duration-fast) var(--ease-out)',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'gold';

// --- Botão TMR ---
export function Btn({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  onClick,
  style,
  title,
  disabled,
}: {
  children: ReactNode;
  variant?: BtnVariant;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
  title?: string;
  disabled?: boolean;
}) {
  const base: CSSProperties = {
    fontFamily: 'var(--font-sans)',
    fontWeight: 500,
    border: 0,
    // Inline vence classe, então `cursor` precisa decidir aqui: a regra `.pa-btn:disabled` do
    // portal.css nunca chegava a valer, e o botão desabilitado seguia com cara de clicável.
    cursor: disabled ? 'default' : 'pointer',
    borderRadius: 'var(--radius-sm)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    transition: 'all var(--duration-fast) var(--ease-out)',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
  };
  const sizes: Record<'sm' | 'md', CSSProperties> = {
    sm: { padding: '7px 12px', fontSize: 13 },
    md: { padding: '9px 16px', fontSize: 14 },
  };
  const variants: Record<BtnVariant, CSSProperties> = {
    primary: { background: 'var(--forest-900)', color: 'var(--cream-50)' },
    secondary: { background: 'var(--cream-50)', color: 'var(--ink-900)', boxShadow: 'inset 0 0 0 1px var(--border-default)' },
    ghost: { background: 'transparent', color: 'var(--fg-muted)' },
    gold: { background: 'var(--gold-400)', color: 'var(--forest-950)' },
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`pa-btn pa-btn--${variant}`}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
    >
      {icon && <StrokeIcon d={icon} size={size === 'sm' ? 15 : 16} />}
      {children}
    </button>
  );
}
